import React, { useRef, useState, useEffect, RefObject } from 'react';
import { Box, Container, Typography, Stack, Chip } from '@mui/material';
import Grid from '@mui/material/Grid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PeopleIcon from '@mui/icons-material/People';
import GavelIcon from '@mui/icons-material/Gavel';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Header from './Header';
import Footer from './Footer';
import SEO from './SEO';
import { trackVideoPlay, trackVideoMilestone, trackVideoComplete, trackKeySection } from './analytics';
import type { SxProps } from '@mui/material';

function useScrollFade(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const fadeStyle = (inView: boolean, delay = 0): SxProps => ({
  opacity: inView ? 1 : 0,
  transform: inView ? 'translateY(0)' : 'translateY(28px)',
  transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
});

// ── Featured grabbing example ────────────────────────────────────────────────

function useVideoTracking(videoRef: RefObject<HTMLVideoElement | null>, videoName: string, pagePath: string) {
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let played = false;
    const hit = new Set<number>();
    const onPlay = () => { if (!played) { played = true; trackVideoPlay(videoName, pagePath); } };
    const onTime = () => {
      if (!video.duration) return;
      const pct = (video.currentTime / video.duration) * 100;
      for (const m of [25, 50]) { if (pct >= m && !hit.has(m)) { hit.add(m); trackVideoMilestone(videoName, m, pagePath); } }
      if (pct >= 75 && !hit.has(75)) { hit.add(75); trackVideoComplete(videoName, pagePath); }
    };
    video.addEventListener('play', onPlay);
    video.addEventListener('timeupdate', onTime);
    return () => { video.removeEventListener('play', onPlay); video.removeEventListener('timeupdate', onTime); };
  }, [videoRef, videoName, pagePath]);
}

function GrabbingExampleCard() {
  const { ref, inView } = useScrollFade(0.1);
  const videoRef = useRef<HTMLVideoElement>(null);
  useVideoTracking(videoRef, 'grabbing-cctv', '/examples');
  useEffect(() => { if (inView) trackKeySection('Detection Examples', '/examples'); }, [inView]);

  const workflowSteps = [
    {
      number: '01',
      label: 'Alert Triggered',
      desc: 'AI detects forceful grabbing in real time and flags the clip instantly.',
      color: '#ff6b6b',
      bg: 'rgba(255,107,107,0.07)',
      border: 'rgba(255,107,107,0.2)',
    },
    {
      number: '02',
      label: 'Human Review',
      desc: 'A designated reviewer watches the flagged clip and makes the call — AI can misread context, so a human always decides.',
      color: '#ff9f43',
      bg: 'rgba(255,159,67,0.07)',
      border: 'rgba(255,159,67,0.2)',
    },
    {
      number: '03',
      label: 'Action',
      desc: 'If confirmed, the reviewer takes one or more actions immediately.',
      color: '#5ce1e6',
      bg: 'rgba(92,225,230,0.07)',
      border: 'rgba(92,225,230,0.2)',
      actions: [
        { icon: <PeopleIcon sx={{ fontSize: 15 }} />, label: 'Report to Staff & Management' },
        { icon: <GavelIcon sx={{ fontSize: 15 }} />, label: 'Report to Law Enforcement' },
        { icon: <FamilyRestroomIcon sx={{ fontSize: 15 }} />, label: 'Notify Parents' },
      ],
    },
  ];

  return (
    <Box
      ref={ref}
      sx={{
        bgcolor: '#13131f',
        border: '1px solid rgba(255,107,107,0.2)',
        borderRadius: 3,
        overflow: 'hidden',
        ...fadeStyle(inView),
      }}
    >
      {/* Top: video + description */}
      <Grid container>
        {/* Video */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Box sx={{ position: 'relative', bgcolor: '#080810', height: { xs: 'auto', md: '100%' }, minHeight: { md: 320 } }}>
            <video
              ref={videoRef}
              src="/grabbing_example_cctv.mp4"
              controls
              muted
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', minHeight: 260 }}
            >
              Your browser does not support embedded videos.
            </video>

            {/* Badge */}
            <Box sx={{ position: 'absolute', top: 12, left: 12, display: 'inline-flex', alignItems: 'center', gap: 0.6, bgcolor: 'rgba(10,10,18,0.88)', border: '1px solid rgba(255,107,107,0.45)', borderRadius: 10, px: 1.5, py: 0.55, backdropFilter: 'blur(8px)' }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#ff6b6b', boxShadow: '0 0 6px #ff6b6b', animation: 'pulse 1.5s infinite', flexShrink: 0 }} />
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, color: '#ff6b6b', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Alert Triggered</Typography>
            </Box>

            {/* Tag */}
            <Box sx={{ position: 'absolute', bottom: 12, right: 12 }}>
              <Chip label="Physical Contact · Forceful Grabbing" size="small" sx={{ bgcolor: 'rgba(10,10,18,0.82)', color: '#9499b0', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 10, fontWeight: 500, border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)', height: 22 }} />
            </Box>
          </Box>
        </Grid>

        {/* Description */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Box sx={{ p: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column', gap: 2.5, height: '100%' }}>
            <Box>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: { xs: 18, md: 21 }, color: '#f0f0f8', lineHeight: 1.3, mb: 0.5 }}>
                Worker grabs child with apparent force
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0' }}>
                Indoor · Physical Contact Detection
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 11, color: '#9499b0', letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.75 }}>What happened</Typography>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>
                A daycare worker is seen grabbing a child in a manner the AI model identifies as forceful — sudden motion, grip pressure, and physical trajectory outside normal care patterns. The interaction is brief but flagged immediately.
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 11, color: '#5ce1e6', letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.75 }}>What SMCLA detected</Typography>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>
                The AI analyzes body movement, speed, directionality, and physical contact force signals. When the pattern crosses the threshold for forceful physical contact, it triggers an alert clip within seconds and notifies the designated reviewer.
              </Typography>
            </Box>

            <Box sx={{ p: 2, bgcolor: 'rgba(255,159,67,0.06)', border: '1px solid rgba(255,159,67,0.15)', borderLeft: '3px solid #ff9f43', borderRadius: 2, mt: 'auto' }}>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 12, color: '#ff9f43', letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.5 }}>Important nuance</Typography>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.7 }}>
                The AI can sometimes misinterpret context — a firm but appropriate safety grab can look similar to a forceful one. That's why every alert goes through human review before any action is taken. The AI flags it; a person decides.
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Bottom: response workflow */}
      <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', p: { xs: 3, md: 4 }, bgcolor: '#0f0f1c' }}>
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 13, color: '#f0f0f8', mb: 3, letterSpacing: '0.02em' }}>
          Response workflow — from alert to action
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 0 }, alignItems: { xs: 'stretch', md: 'stretch' } }}>
          {workflowSteps.map((step, i) => (
            <React.Fragment key={step.number}>
              <Box
                sx={{
                  flex: 1,
                  p: 2.5,
                  bgcolor: step.bg,
                  border: `1px solid ${step.border}`,
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 11, color: step.color, letterSpacing: '0.1em' }}>
                    {step.number}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 14, color: step.color }}>
                    {step.label}
                  </Typography>
                </Box>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.65 }}>
                  {step.desc}
                </Typography>

                {/* Action options for step 3 */}
                {step.actions && (
                  <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                    {step.actions.map((action) => (
                      <Box key={action.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ color: '#5ce1e6', display: 'flex', alignItems: 'center', flexShrink: 0 }}>{action.icon}</Box>
                        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: '#d0d0e0', fontWeight: 500 }}>
                          {action.label}
                        </Typography>
                      </Box>
                    ))}
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, color: 'rgba(148,153,176,0.6)', mt: 0.25 }}>
                      One or more — reviewer's decision. The AI flags; a human confirms before any clip is shared externally. Only confirmed alerts with chain-of-custody metadata are exported to authorities.
                    </Typography>
                  </Stack>
                )}
              </Box>

              {/* Arrow between steps */}
              {i < workflowSteps.length - 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: { xs: 0, md: 1.5 }, py: { xs: 0.5, md: 0 }, flexShrink: 0 }}>
                  <ArrowForwardIcon sx={{ color: 'rgba(148,153,176,0.3)', fontSize: 18, transform: { xs: 'rotate(90deg)', md: 'none' } }} />
                </Box>
              )}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// ── Standard example cards ───────────────────────────────────────────────────

type AlertStatus = 'no-alert' | 'alert';

interface Example {
  id: number;
  videoSrc: string;
  status: AlertStatus;
  tag: string;
  title: string;
  what: string;
  detected: string;
  verdict: string;
}

const examples: Example[] = [
  {
    id: 1,
    videoSrc: '/high_resolution_daycare_cctv_c.mp4',
    status: 'no-alert',
    tag: 'Indoor · Distress + Caregiver Response',
    title: 'Child cries after blocks fall — worker responds immediately',
    what: "A toddler's building blocks collapse and he begins crying. A nearby daycare worker immediately notices, walks over, and crouches down to comfort him — the interaction lasts under 30 seconds before the child is calm.",
    detected: "SMCLA detects the child's crying onset and simultaneously tracks the caregiver's proximity and movement toward the child. The response time falls well within the expected threshold.",
    verdict: 'No alert sent. The system recognizes this as attentive care — a child in distress with an immediate, engaged caregiver response. This is exactly the false-positive scenario SMCLA is built to avoid.',
  },
  {
    id: 2,
    videoSrc: '/high_resolution_daycare_outdoo.mp4',
    status: 'alert',
    tag: 'Outdoor · Unsupervised Children',
    title: 'Multiple children active outdoors — no caregiver present',
    what: 'An outdoor play area shows several young children running and playing. No daycare worker is visible anywhere within the monitored zone. The scene continues with no adult presence detected.',
    detected: 'SMCLA detects active child presence in the zone and simultaneously registers zero caregiver presence. A supervision timer begins the moment adult coverage drops to zero.',
    verdict: 'Alert triggered and sent to the facility owner. Unsupervised children in an outdoor area is a regulatory violation and a direct safety risk — a fall, choking incident, or conflict would have no adult response. The alert arrives before any incident can occur.',
  },
];

const statusConfig = {
  'no-alert': {
    color: '#4ade80',
    bgColor: 'rgba(74,222,128,0.06)',
    borderColor: 'rgba(74,222,128,0.15)',
    accentBorder: '#4ade80',
    label: 'No Alert',
    verdictLabel: 'Why no alert',
  },
  'alert': {
    color: '#ff6b6b',
    bgColor: 'rgba(255,107,107,0.06)',
    borderColor: 'rgba(255,107,107,0.15)',
    accentBorder: '#ff6b6b',
    label: 'Alert Triggered',
    verdictLabel: 'Why it alerts',
  },
};

function ExampleCard({ example, delay }: { example: Example; delay: number }) {
  const { ref, inView } = useScrollFade();
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoName = example.videoSrc.replace('/', '').replace('.mp4', '');
  useVideoTracking(videoRef, videoName, '/examples');
  const cfg = statusConfig[example.status];

  return (
    <Box
      ref={ref}
      sx={{
        bgcolor: '#13131f',
        border: `1px solid ${cfg.borderColor}`,
        borderRadius: 3,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        ...fadeStyle(inView, delay),
      }}
    >
      <Box sx={{ position: 'relative', bgcolor: '#080810' }}>
        <video ref={videoRef} src={example.videoSrc} controls muted playsInline style={{ width: '100%', maxHeight: 360, objectFit: 'cover', display: 'block' }}>
          Your browser does not support embedded videos.
        </video>

        <Box sx={{ position: 'absolute', top: 12, left: 12, display: 'inline-flex', alignItems: 'center', gap: 0.6, bgcolor: 'rgba(10,10,18,0.88)', border: `1px solid ${cfg.color}55`, borderRadius: 10, px: 1.5, py: 0.55, backdropFilter: 'blur(8px)' }}>
          {example.status === 'alert' && <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: cfg.color, boxShadow: `0 0 6px ${cfg.color}`, animation: 'pulse 1.5s infinite', flexShrink: 0 }} />}
          {example.status === 'no-alert' && <CheckCircleIcon sx={{ fontSize: 13, color: cfg.color }} />}
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, color: cfg.color, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{cfg.label}</Typography>
        </Box>

        <Box sx={{ position: 'absolute', bottom: 12, right: 12 }}>
          <Chip label={example.tag} size="small" sx={{ bgcolor: 'rgba(10,10,18,0.82)', color: '#9499b0', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 10, fontWeight: 500, border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)', height: 22 }} />
        </Box>
      </Box>

      <Box sx={{ p: { xs: 3, md: 3.5 }, flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 18, color: '#f0f0f8', lineHeight: 1.35 }}>
          {example.title}
        </Typography>

        <Box>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 11, color: '#9499b0', letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.75 }}>What happened</Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>{example.what}</Typography>
        </Box>

        <Box>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 11, color: '#5ce1e6', letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.75 }}>What SMCLA detected</Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>{example.detected}</Typography>
        </Box>

        <Box sx={{ p: 2, bgcolor: cfg.bgColor, border: `1px solid ${cfg.borderColor}`, borderLeft: `3px solid ${cfg.accentBorder}`, borderRadius: 2, mt: 'auto' }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 12, color: cfg.color, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.5 }}>{cfg.verdictLabel}</Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.7 }}>{example.verdict}</Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function ExamplesPage() {
  const heroSection = useScrollFade(0.1);

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <SEO
        title="Detection Examples — See SMCLA in Action"
        description="Real CCTV footage showing what SMCLA catches and what it correctly ignores. Forceful grabbing, unsupervised children, healthy caregiver interactions — see how the AI and human review work together."
        path="/examples"
      />
      <Header />

      {/* Hero */}
      <Box
        ref={heroSection.ref}
        sx={{ pt: { xs: 8, md: 12 }, pb: { xs: 6, md: 8 }, px: 3, textAlign: 'center', position: 'relative', overflow: 'hidden', ...fadeStyle(heroSection.inView) }}
      >
        <Box sx={{ position: 'absolute', top: '-20%', left: '50%', transform: 'translateX(-50%)', width: 700, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(92,225,230,0.1) 0%, transparent 70%)', filter: 'blur(40px)', pointerEvents: 'none' }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.2)', borderRadius: 10, px: 2, py: 0.75, mb: 3 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#5ce1e6' }} />
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 11, color: '#5ce1e6', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Real CCTV Footage</Typography>
          </Box>
          <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2rem', sm: '2.8rem', md: '3.5rem' }, letterSpacing: '-0.03em', lineHeight: 1.1, color: '#f0f0f8', mb: 2.5 }}>
            See what the AI catches.<br />
            <Box component="span" sx={{ color: '#5ce1e6' }}>And what it correctly ignores.</Box>
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: { xs: 15, md: 17 }, color: '#9499b0', maxWidth: 580, mx: 'auto', lineHeight: 1.65, mb: 1.5 }}>
            Real footage from daycare CCTV cameras. Each example shows the scenario, what SMCLA detected, and the response — including how human review works alongside the AI.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: 'rgba(148,153,176,0.5)' }}>
            Footage used with facility permission. Faces blurred for privacy.
          </Typography>
        </Container>
      </Box>

      {/* Examples */}
      <Box sx={{ pb: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="lg">
          <Stack spacing={4}>

            {/* Featured: grabbing example — full width, top */}
            <GrabbingExampleCard />

            {/* Standard pair — side by side */}
            <Grid container spacing={3} alignItems="stretch">
              {examples.map((ex, i) => (
                <Grid key={ex.id} size={{ xs: 12, md: 6 }}>
                  <ExampleCard example={ex} delay={i * 0.1} />
                </Grid>
              ))}
            </Grid>

          </Stack>

          {/* Coming soon strip */}
          <Box sx={{ mt: 6, p: 3, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3, textAlign: 'center' }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#5ce1e6', opacity: 0.5 }} />
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 12, color: '#9499b0', letterSpacing: '0.08em', textTransform: 'uppercase' }}>More examples being added</Typography>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#5ce1e6', opacity: 0.5 }} />
            </Box>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: 'rgba(148,153,176,0.6)', maxWidth: 460, mx: 'auto', lineHeight: 1.65 }}>
              Verbal escalation, extended crying without response, ratio violations, and more — real footage is reviewed and added as facilities share their cases.
            </Typography>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
